import {
  BarsOutlined,
  DashboardOutlined,
  LogoutOutlined,
  MailOutlined,
  RightOutlined,
  SettingOutlined,
  UnorderedListOutlined
} from "@ant-design/icons";
import {
  RefineThemedLayoutV2SiderProps,
  ThemedTitleV2,
  useThemedLayoutContext
} from "@refinedev/antd";
import {
  CanAccess,
  ITreeMenu,
  pickNotDeprecated,
  useActiveAuthProvider,
  useGetIdentity,
  useIsExistAuthentication,
  useLink,
  useLogout,
  useMenu,
  useRouterContext,
  useRouterType,
  useTitle,
  useTranslate,
  useWarnAboutChange
} from "@refinedev/core";
import { Avatar, Button, Card, Drawer, Grid, Layout, Menu, theme, Typography } from "antd";
import logoutIcon from "assets/logoutIcon.png";
import { Logout } from "components/Auth/Logout/Logout";
import StagingAuthContext from "context/StagingAuthContext";
import AddCreditsModal from "modals/AddCreditsModal";
import React, { CSSProperties, useContext, useState } from "react";
import { COLOR_PRIMARY_ORANGE } from "theme/colors";
import { drawerButtonStyles } from "./styles";
import { CustomTitle } from "./title";
import TotalCredits from "./TotalCredits";
import { QUIX_SALES_EMAIL } from "constants/common";
import { useUserType } from "hooks/useUserType";
import { UserIdentityData } from "types/props";

const { SubMenu } = Menu;
const { useToken } = theme;

export const SiderCS: React.FC<RefineThemedLayoutV2SiderProps> = ({
  Title: TitleFromProps,
  render,
  meta,
  fixed,
  activeItemDisabled = false
}) => {
  const { token } = useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext();

  const isExistAuthentication = useIsExistAuthentication();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { setWarnWhen } = useWarnAboutChange();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === "legacy" ? LegacyLink : NewLink;
  const TitleFromContext = useTitle();
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const breakpoint = Grid.useBreakpoint();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy)
  });
  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  const RenderToTitle = CustomTitle ?? TitleFromContext ?? ThemedTitleV2;
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const { data: user } = useGetIdentity<{ role: { type: string }; approved?: boolean }>();
  const isAdmin = user?.role?.type === "admin";
  const { isInternalUser } = useUserType();
  const [visible, setVisible] = useState(false);
  const { setIsLoggedIn } = useContext(StagingAuthContext);
  const [selectedTab, setSelectedTab] = useState<"SELECT_PLAN" | "CREDITS_CALCULATOR">(
    "SELECT_PLAN"
  );

  const renderTreeView = (tree: ITreeMenu[], selectedKey?: string) => {
    return tree.map((item: ITreeMenu) => {
      const { icon, label, route, key, name, children, parentName, meta, options } = item;

      if (children.length > 0) {
        return (
          <CanAccess
            key={item.key}
            resource={name.toLowerCase()}
            action="list"
            params={{
              resource: item
            }}>
            <SubMenu key={item.key} icon={icon ?? <UnorderedListOutlined />} title={label}>
              {renderTreeView(children, selectedKey)}
            </SubMenu>
          </CanAccess>
        );
      }
      const isSelected = key === selectedKey;
      const isRoute = !(
        pickNotDeprecated(meta?.parent, options?.parent, parentName) !== undefined &&
        children.length === 0
      );

      const linkStyle: CSSProperties =
        activeItemDisabled && isSelected ? { pointerEvents: "none" } : {};

      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{
            resource: item
          }}>
          <Menu.Item
            key={item.key}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
            style={{
              ...linkStyle,
              minHeight: "44px"
            }}>
            <Link to={route ?? ""} style={linkStyle}>
              {label}
            </Link>
            {!siderCollapsed && isSelected && <div className="ant-menu-tree-arrow" />}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  const handleLogout = async () => {
    setWarnWhen(false);
    setIsLoggedIn(false);
    mutateLogout();
  };

  const logout = isExistAuthentication && (
    <Menu.Item key="logout" onClick={() => handleLogout()} icon={<LogoutOutlined />}>
      {translate("buttons.logout", "Logout")}
    </Menu.Item>
  );

  const dashboard = false ? (
    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
      <Link to="/">{translate("dashboard.title", "Dashboard")}</Link>
      {!siderCollapsed && selectedKey === "/" && <div className="ant-menu-tree-arrow" />}
    </Menu.Item>
  ) : null;

  const items = renderTreeView(menuItems, selectedKey);

  if (isAdmin) {
    items.push(
      <Menu.Item
        key={"admin-logout"}
        icon={
          <div style={{ padding: "8px" }}>
            <LogoutOutlined />
          </div>
        }
        onClick={() => setVisible(!visible)}>
        <span>Logout</span>
      </Menu.Item>
    );
  }

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed: siderCollapsed
      });
    }
    return (
      <>
        {dashboard}
        {items}
        {/* {logout} */}
      </>
    );
  };

  const { data: { firstName, lastName } = {} } = useGetIdentity<UserIdentityData>();

  const UserTile = (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: token.colorBgElevated,
          fontSize: "14px"
        }}>
        <hr
          style={{
            backgroundColor: "#475467",
            margin: "12px 8px",
            border: "0",
            height: "1px",
            width: "calc(100% - 28px)"
          }}
        />
        <div
          style={{
            display: "flex",
            paddingLeft: "18px",
            alignItems: "center",
            marginBottom: "12px",
            width: "100%"
          }}>
          <Avatar
            style={{
              marginRight: "8px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
            {firstName?.[0]?.toUpperCase() || "Q"}
          </Avatar>
          <Typography.Text
            ellipsis
            style={{ maxWidth: !isAdmin ? "160px" : "180px", fontSize: "20px" }}>
            {firstName || ""} {lastName || ""}
          </Typography.Text>
          {!isAdmin && (
            <img
              alt="logout-icon"
              src={logoutIcon}
              onClick={() => {
                setVisible(!visible);
              }}
              style={{
                color: "white",
                cursor: "pointer",
                marginLeft: "auto",
                marginRight: "8px"
              }}
            />
          )}
        </div>
      </div>

      <AddCreditsModal
        showAddCreditsModal={showAddCreditsModal}
        setShowAddCreditsModal={setShowAddCreditsModal}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      <>
        <Logout close={() => setVisible(false)} open={visible} />
      </>
    </>
  );

  const renderMenu = () => {
    return (
      <Menu
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={defaultOpenKeys}
        mode="inline"
        style={{
          paddingLeft: "8px",
          paddingRight: "8px",
          border: "none",
          overflow: "auto",
          height: "calc(100vh - 15vh)",
          display: "flex",
          flexDirection: "column"
        }}
        onClick={() => {
          setMobileSiderOpen(false);
        }}>
        {renderSider()}
        <div style={{ marginTop: "auto" }}>
          {
            <>
              {!isAdmin && !isInternalUser && (
                <Card
                  style={{
                    backgroundColor: "#202440",
                    borderRadius: "8px",
                    borderColor: "#202440",
                    margin: "12px 0"
                  }}
                  styles={{
                    body: {
                      padding: "20px 16px 20px 16px"
                    }
                  }}>
                  <Typography.Title
                    style={{
                      fontSize: "20px",
                      fontWeight: 500,
                      margin: 0,
                      padding: 0
                    }}>
                    QuiX credits
                  </Typography.Title>
                  <Typography.Text
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#C1C2C3",
                      lineHeight: "20px"
                    }}>
                    You need to add credits in order to create jobs
                  </Typography.Text>
                  <TotalCredits />
                  <Typography.Text
                    onClick={() => {
                      if (isMobile) {
                        setMobileSiderOpen(false);
                      }

                      setShowAddCreditsModal(true);
                    }}
                    style={{
                      textAlign: "end",
                      display: "block",
                      width: "100%",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: COLOR_PRIMARY_ORANGE,
                      cursor: "pointer"
                    }}>
                    Add Credits <RightOutlined style={{ marginLeft: "12px" }} />
                  </Typography.Text>
                </Card>
              )}
              {user?.role?.type !== "admin" && (
                <Menu.Item
                  key={"/contact"}
                  icon={
                    <div style={{ padding: "8px" }}>
                      <MailOutlined />
                    </div>
                  }
                  prefix="contact">
                  <a href={`mailto:${QUIX_SALES_EMAIL}`}>{"Contact us via Email"}</a>
                  {selectedKey === "/contact" && <div className="ant-menu-tree-arrow" />}
                </Menu.Item>
              )}
              <Menu.Item
                key={"/settings"}
                style={{
                  backgroundColor: selectedKey === "/settings" ? "#283583" : ""
                }}
                icon={
                  <div style={{ padding: "8px" }}>
                    <SettingOutlined />
                  </div>
                }
                prefix="settings">
                <Link to={"/settings"}>{"Settings"}</Link>
                {selectedKey === "/settings" && <div className="ant-menu-tree-arrow" />}
              </Menu.Item>
            </>
          }
        </div>
        {UserTile}
      </Menu>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={300}
          styles={{
            body: {
              padding: 0,
              margin: 0
            }
          }}
          maskClosable={true}>
          <>
            <Layout.Sider
              width={"100%"}
              style={{
                flex: 1,
                backgroundColor: token.colorBgContainer,
                borderRight: `1px solid ${token.colorBgElevated}`
              }}>
              <div
                style={{
                  width: "100%",
                  padding: "0 16px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "64px"
                }}>
                <RenderToTitle collapsed={false} />
              </div>
              {renderMenu()}
              {/* {UserTile} */}
            </Layout.Sider>
          </>
        </Drawer>
        <Button
          style={drawerButtonStyles}
          size="large"
          onClick={() => setMobileSiderOpen(true)}
          icon={<BarsOutlined />}
        />
      </>
    );
  };

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid #2C2C2C`,
    maxHeight: "100vh"
  };

  if (fixed) {
    siderStyles.position = "fixed";
    siderStyles.top = 0;
    siderStyles.height = "100vh";
    siderStyles.zIndex = 999;
  }

  return (
    <>
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? "80px" : "279px",
            transition: "all 0.2s"
          }}
        />
      )}
      <Layout.Sider
        style={siderStyles}
        width={279}
        collapsed={siderCollapsed}
        onCollapse={(collapsed, type) => {
          if (type === "clickTrigger") {
            setSiderCollapsed(collapsed);
          }
        }}
        collapsedWidth={80}
        breakpoint="lg">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "15vh",
            backgroundColor: token.colorBgElevated,
            fontSize: "14px"
          }}>
          <RenderToTitle collapsed={siderCollapsed} />
        </div>
        {renderMenu()}
        {/* {UserTile} */}
      </Layout.Sider>
    </>
  );
};
